import { OPTION_STATUS, NA, PageEnum, OMS_FORMAT } from "@/enums/pageEnum";
import { FilterParams, TelemetryItem } from "@/facilities/model/user";
import { ProFormSelect, ProFormDatePicker } from "@ant-design/pro-components";
import { HView, Status, SvgIcon } from "@/components";
import dayjs, { Dayjs } from "@/utils/dayjsConfig";
import { Popover } from "antd";
import { isArray } from "lodash-es";

export const PaginationConfig = () => ({
  total: 500, // 总条数
  current: 1, // 当前页
  pageSize: 20,
  size: "default" as "default" | "small",
  showTotal: () => <></>,
  showSizeChanger: false,
  showQuickJumper: false,
});

export const UserAndOrganFilter = (
  name: string,
  status: string
): FilterParams[] => {
  return [
    { field: "name", label: name, component: "text" },
    {
      field: "status",
      label: status,
      component: "select",
      fieldProps: { style: { width: 150 } },
      options: OPTION_STATUS,
    },
  ];
};

export const GetEamilInput = (label: string) => {
  return (
    <ProFormSelect
      name="emails"
      label={label}
      className="multipleEmail"
      rules={[
        {
          required: true,
          validator(_, value) {
            if (!value || !value.length)
              return Promise.reject(new Error("Please enter email"));
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            let flag = false;
            if (isArray(value)) {
              value.map((item: string) => {
                if (/\s/.test(item) || !emailRegex.test(item)) {
                  flag = true;
                }
              });
            }
            if (flag) {
              return Promise.reject(
                new Error("Please enter the correct email")
              );
            }
            return Promise.resolve();
          },
        },
      ]}
      fieldProps={{
        mode: "tags",
        popupClassName: "emailClassName",
      }}
    />
  );
};

export const LiftColumns = (isAddlink?: boolean, isLmdlink?: boolean) => [
  {
    dataIndex: "name",
    renderText: (_: string, record: { id: string }) =>
      isAddlink ? (
        _
      ) : (
        <HView
          name={_}
          path={`${PageEnum.LIFT}/detail/${record?.id}`}
          state={record}
          isNewWindow
        />
      ),
  },
  {
    dataIndex: "lmd.attribute.clientId",
    renderText: (_: string, record: { lmd: { id: string } }) =>
      isLmdlink ? (
        _
      ) : (
        <HView
          name={_}
          path={`${PageEnum.LMD}/detail/${record?.lmd?.id}`}
          state={record}
          isNewWindow
        />
      ),
  },
];

/**
 * AlarmRbeColumns
 *
 * @param {object} options
 * @param {boolean} [options.isLiftlink=true] - Whether to link to the lift detail page
 * @param {boolean} [options.isLmdlink=true] - Whether to link to the LMD detail page
 * @returns {array} Ant Design Pro Table columns
 */
export const AlarmRbeColumns = ({
  isLmdlink = true,
  isLiftlink = true,
}: {
  t: any;
  isLiftlink?: boolean;
  isLmdlink?: boolean;
}) => [
  {
    dataIndex: "lift.name",
    renderText: (_: string, record: { lift: { id: string; name: string } }) =>
      isLiftlink ? (
        <HView
          name={record?.lift?.name}
          path={`${PageEnum.LIFT}/detail/${record?.lift?.id}`}
          state={record}
          isNewWindow
        />
      ) : (
        record.lift?.name
      ),
  },
  {
    dataIndex: "lmd.attribute.clientId",
    renderText: (_: string, record: { lmd: { id: string; name: string } }) =>
      isLmdlink ? (
        <HView
          name={_}
          path={`${PageEnum.LMD}/detail/${record?.lmd?.id}`}
          state={record}
          isNewWindow
        />
      ) : (
        record.lmd?.name
      ),
  },
  {
    dataIndex: "type",
    renderText: (_: string, record: { description: string }) =>
      record?.description ? (
        <Popover content={<p>{record?.description}</p>} title="">
          {_}
        </Popover>
      ) : (
        _
      ),
  },
  {
    dataIndex: "status",
    renderText: (_: string) => <Status status={_} />,
  },
];

export const RbeStatuses = (t: any) => [
  { value: "SET", label: t("SET") },
  { value: "CLEAR", label: t("CLEAR") },
];

export const GetAlarmStatus = (t: any) => {
  return {
    title: t("alarmStatus"),
    dataIndex: "alarmStatusFormat",
    renderText: (_: string) => {
      if (_ === NA) return NA;
      return <Status status={_} />;
    },
  };
};

export const GetLiftStatusCols = (t: any, liftFild?: any) => {
  const lists = [
    "Complaint",
    "LB",
    "LJ",
    "OMS",
    "RO",
    "PSF",
    "LM",
    "MOF",
    "ARDI",
    "CF",
    "LMDC",
  ];
  const cols: any[] = [
    liftFild
      ? liftFild
      : {
          title: t("lift"),
          align: "center",
          dataIndex: ["lift", "attribute", "liftNumber"],
        },
  ];
  lists.forEach((item) => {
    cols.push({
      title: item,
      dataIndex: item,
      align: "center",
      renderText: (_: string, record: any) => {
        if (
          (item === "Complaint" && !record.complaintCnt) ||
          (item !== "Complaint" &&
            item !== "OMS" &&
            record.lift?.telemetries?.find(
              (k: TelemetryItem) => k?.key === item
            )?.value !== "true") ||
          (item === "OMS" && !record.omsScheduleCnt)
        ) {
          return null;
        }
        return (
          <span className="bg-[#F00] rounded-full w-[10px] h-[10px] inline-block"></span>
        );
      },
    });
  });
  return cols;
};

export interface ConfigCustomDate {
  name: string;
  minDate: Date;
  label?: string;
  maxDate?: Date;
  onChange?: (val: Dayjs) => void;
}

const generateArray = (start: number, end: number) => {
  return Array.from({ length: end - start }, (_, i) => start + i);
};

export const CustomDatePickerFunc = ({
  name,
  minDate,
  label,
  maxDate,
  onChange,
}: ConfigCustomDate) => {
  return (
    <ProFormDatePicker
      name={name}
      disabled={false}
      showTime={{
        disabledTime: (now: Dayjs) => {
          const isToday = new Date(minDate).setHours(0, 0, 0, 0);
          const nowDay = now.toDate().setHours(0, 0, 0, 0);
          let maxTiem: number;
          if (maxDate) {
            maxTiem = new Date(maxDate).setHours(0, 0, 0, 0);
          }
          return {
            disabledHours: () => {
              const minHs = generateArray(0, minDate.getHours());
              if (maxDate) {
                const maxHs = generateArray(maxDate.getHours() + 1, 24);
                if (maxTiem === nowDay && isToday === nowDay) {
                  return [...minHs, ...maxHs];
                } else if (maxTiem === nowDay) {
                  return maxHs;
                }
              }
              if (isToday === nowDay) {
                return minHs;
              }
              return [];
            },
            disabledMinutes: (selectedHour: number) => {
              const minMs = generateArray(0, minDate.getMinutes());
              const isMinToday =
                isToday === nowDay && minDate.getHours() === selectedHour;
              if (maxDate) {
                const maxM = maxDate.getMinutes();
                const isMaxToday =
                  maxTiem === nowDay && maxDate.getHours() === selectedHour;
                if (isMinToday && isMaxToday) {
                  return [...minMs, ...generateArray(maxM + 1, 60)];
                } else if (isMaxToday) {
                  return generateArray(maxM, 60);
                }
              }
              if (isMinToday) return minMs;
              return [];
            },
          };
        },
      }}
      rules={[{ required: true, message: "Please select a time." }]}
      onChange={(e: Dayjs) => {
        onChange?.(e);
      }}
      fieldProps={{
        className: "w-full",
        allowClear: false,
        inputReadOnly: true,
        format: OMS_FORMAT,
        minDate: dayjs(minDate),
        maxDate: maxDate ? dayjs(maxDate) : null,
      }}
      label={label}
    />
  );
};

export const GetExpandIcon = (isActive?: boolean, width?: number) => (
  <SvgIcon classa={isActive ? "up" : "down"} style={{ width: width || 22 }} />
);

const getComsPub = (t: any): FilterParams => ({
  field: "name",
  label: t("lift"),
  placeholder: ["TC", "PFX", "BLK", "LIFT"],
  component: "groupTexts",
});

export const LiftTcPfxBlkNumber = (t: any): FilterParams => ({
  ...getComsPub(t),
  keys: ["tc", "pfx", "blk", "liftNumber"],
});

export const LiftTcLongSuffix = (t: any): FilterParams => ({
  ...getComsPub(t),
  keys: ["liftTc", "liftPfx", "liftBlk", "liftNumber"],
});
